/**
 * ScopeSync — keeps the scope and the URL agreeing, in both directions.
 *
 * Scope used to live only in React context plus localStorage, which meant a link to a scoped
 * page carried no scope: refresh it and you got whatever site that browser last happened to
 * pick, and a link pasted to a colleague opened on THEIR site. For an operator sending
 * "look at this" during an incident, that is the difference between a useful link and a
 * misleading one.
 *
 * So `?site=<nodeId>` (or `?site=all`) rides on every scoped URL:
 *
 *   URL → scope   on arrival, an explicit `?site=` wins over the stored selection. This is what
 *                 makes a shared link land where its sender was.
 *   scope → URL   afterwards, the param is kept in step with the switcher, replacing rather
 *                 than pushing so the back button steps through pages, not through scope edits.
 *
 * The param is only written on scoped pages. Platform-admin pages read the cloud's own database
 * and mean the same thing under any scope, so tagging them would be noise.
 *
 * Renders nothing — it's a synchroniser, mounted once by the Layout.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useNode } from '../../contexts/NodeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  ALL_SITES,
  SCOPE_PARAM,
  isAllSitesPath,
  isUnscoped,
  nodePathScope,
  readStoredScope,
  resolveScopeSwitch,
} from '../../constants/scope';

export default function ScopeSync() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { nodes, selectedNodeId, selectNode, loading } = useNode();
  const { user } = useAuth();

  const urlScope = params.get(SCOPE_PARAM);
  const currentScope = selectedNodeId ?? ALL_SITES;

  // Adopt an incoming `?site=` ONCE per value. Without this latch, the scope→URL effect below
  // and this one would take turns overwriting each other every render.
  const adoptedRef = useRef<string | null>(null);

  /**
   * Open a fresh session on the user's favourite site, if they have one.
   *
   * Runs ONCE, and only from a standing start: nothing stored for this session
   * (`readStoredScope()` undefined, which is what logout leaves behind).
   *
   * `?site=` is NOT treated as a blanket veto, and that distinction is the whole reason this
   * works. The scope→URL effect below stamps the CURRENT scope into the URL on every
   * navigation, so `?site=all` is usually this component's own echo rather than anything the
   * operator asked for — and reading it as a request meant that after one navigation the URL
   * permanently said "All Sites", and the favourite could never apply again. A reload always
   * landed on All Sites no matter what was starred.
   *
   * Exactly two things outrank it, and both name a SPECIFIC site:
   *
   *   • `?site=<id>` in the URL — unambiguously a request; a shared deep link must open where
   *     it points. `?site=all` does NOT count: the scope→URL effect below stamps the current
   *     scope into the URL on every navigation, so `all` there is usually this component's own
   *     echo rather than anything the operator asked for.
   *   • a stored site from this session — so an in-session switch survives navigation.
   *
   * Being on All Sites outranks nothing. It is not stored, so a reload starts from the
   * favourite again — which is what makes it a default rather than a one-time redirect.
   */
  const defaultAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultAppliedRef.current || loading || !user) return;
    const favourite = user.default_node_id;
    if (!favourite) return;
    const linkedToASite = !!urlScope && urlScope !== ALL_SITES;
    if (linkedToASite || readStoredScope()) {
      // A specific site is already in force; never reconsider.
      defaultAppliedRef.current = true;
      return;
    }
    defaultAppliedRef.current = true;
    if (!nodes.some((n) => n.nodeId === favourite)) return;

    // Claim the stale `?site=all` before switching. The adoption effect below would otherwise
    // see an unadopted `all` and call selectNode(null), undoing this on the very next render —
    // and whether it did would depend on React's batching, which is not something to leave a
    // scope decision resting on.
    if (urlScope === ALL_SITES) adoptedRef.current = ALL_SITES;
    selectNode(favourite);
    // The sidebar follows scope on its own, but the PAGE does not: landing on the all-sites
    // dashboard with a site's menu beside it is the ambiguity this whole module exists to
    // prevent. Move to the site-scope counterpart of wherever we are.
    if (isAllSitesPath(pathname)) {
      const target = resolveScopeSwitch(pathname, false);
      if (target) navigate(target, { replace: true });
    }
  }, [loading, user, nodes, urlScope, pathname, selectNode, navigate]);

  useEffect(() => {
    if (!urlScope || adoptedRef.current === urlScope) return;

    if (urlScope === ALL_SITES) {
      adoptedRef.current = urlScope;
      if (selectedNodeId) selectNode(null);
      return;
    }

    // Wait for the node list before judging an unknown id — on a cold load the list is still in
    // flight, and rewriting the URL now would silently discard a perfectly good shared link.
    if (loading) return;
    adoptedRef.current = urlScope;
    if (nodes.some((n) => n.nodeId === urlScope)) {
      if (selectedNodeId !== urlScope) selectNode(urlScope);
    }
    // An id that isn't ours (revoked access, decommissioned site) falls through: the scope→URL
    // effect below rewrites the param to whatever scope is actually in force.
  }, [urlScope, loading, nodes, selectedNodeId, selectNode]);

  /**
   * scope → URL: stamp `?site=` to match the switcher.
   *
   * DECLARED BEFORE THE TWO REDIRECTS BELOW, and that order is load-bearing. `setParams` writes
   * the query onto the pathname of the render it was called in, so when a scope change makes
   * this effect AND a redirect fire in the same commit, whichever runs last decides the URL —
   * and this one, running last, used to put the old pathname back and silently undo the
   * redirect. Switching site on Site Health kept the old node id; switching to All Sites on a
   * per-site page kept the page. Running the redirects last means the last write is the
   * navigation, and this effect re-stamps the param on the path it lands on.
   */
  useEffect(() => {
    if (isUnscoped(pathname)) {
      if (!urlScope) return;
      const next = new URLSearchParams(params);
      next.delete(SCOPE_PARAM);
      setParams(next, { replace: true });
      return;
    }

    // Hold off while a pending `?site=` is still being adopted, or the stale scope would be
    // written back over the link the operator just opened.
    if (urlScope && adoptedRef.current !== urlScope) return;
    if (urlScope === currentScope) return;

    const next = new URLSearchParams(params);
    next.set(SCOPE_PARAM, currentScope);
    adoptedRef.current = currentScope;
    setParams(next, { replace: true });
  }, [pathname, currentScope, urlScope, params, setParams]);

  /**
   * Some pages carry the site in the PATH rather than in `?site=` — Site Health
   * (`/node-monitor/<id>`) and anything addressed as `/nodes/<id>/cameras/…`. They fetch from
   * the path, so the path and the scope must agree, and this is what keeps them agreeing:
   *
   *   path → scope   a link naming another site is a request, exactly as `?site=<id>` is: adopt
   *                  it, once per value (the same latch `?site=` uses). This is what makes
   *                  "Inspect" on Platform Health, and a node row on the estate dashboard, open
   *                  the site they name.
   *   scope → path   afterwards the page follows the title bar. Site Health re-points at the new
   *                  site; a camera address has no counterpart there, so it falls back to that
   *                  site's camera list.
   *
   * Without this, switching site on Site Health left the old node id in the path and the page
   * went on polling the site the operator had just navigated away from.
   */
  const pathNode = nodePathScope(pathname);
  const pathNodeId = pathNode?.nodeId ?? null;
  const adoptedPathRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pathNode || loading) return;
    if (adoptedPathRef.current !== pathNode.nodeId) {
      // Newly arrived at a path naming a site: that path is the request.
      adoptedPathRef.current = pathNode.nodeId;
      if (selectedNodeId !== pathNode.nodeId && nodes.some((n) => n.nodeId === pathNode.nodeId)) {
        selectNode(pathNode.nodeId);
      }
      return;
    }
    if (selectedNodeId && selectedNodeId !== pathNode.nodeId) {
      adoptedPathRef.current = selectedNodeId;
      navigate(pathNode.next(selectedNodeId), { replace: true });
    }
    // pathNode is derived from pathname, so its identity changes every render — the id is what
    // this effect actually keys off.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathNodeId, loading, nodes, selectedNodeId, selectNode, navigate]);

  /**
   * A per-site page opened while the scope is All Sites has no site to fetch from — it would
   * render a "select a site" empty state pointing at a control the operator has already used.
   * Send them to the all-sites counterpart instead, by the same rules the switcher uses.
   *
   * Held until the node list has settled, so a stored site still resolving doesn't get read as
   * All Sites and bounce a perfectly valid deep link.
   */
  useEffect(() => {
    if (loading || selectedNodeId) return;
    if (isUnscoped(pathname) || isAllSitesPath(pathname)) return;
    // A path naming one of our own sites is never bounced for want of a scope: the effect above
    // adopts it, and both effects see the same (still null) scope in that commit — so testing
    // the latch here would race with it. Opening a link to one site's health page while on All
    // Sites has to land on that page, not on the estate dashboard. An id that ISN'T ours can't
    // be adopted, so it still gets bounced.
    if (pathNodeId && nodes.some((n) => n.nodeId === pathNodeId)) return;
    const target = resolveScopeSwitch(pathname, true);
    if (target) navigate(target, { replace: true });
  }, [loading, selectedNodeId, pathname, pathNodeId, nodes, navigate]);


  return null;
}
